import { z } from "zod";

import * as DbEnum from "../../generated/db-enum";
import { enumReadSchema } from "../../shared/core/enum-read.schema";

/**
 * App visibility — who may reach the deployed app over its public URL.
 *
 * - `PRIVATE`: the app carries a per-app edge-auth token, and the edge admits
 *   only a caller that presents it in `X-Vibe-App-Token` or that holds a browser
 *   session minted for this app. Anyone else is refused with a 401 that says
 *   why. The platform injects the token on agent tool calls, so a private app is
 *   effectively agent-tool-only.
 * - `PUBLIC`: no edge-auth token, and the edge admits anyone with the URL (a
 *   browser-viewable site / dashboard / docs / public webhook). No app-level auth.
 *
 * Routing is identical either way, and nothing about it encodes the visibility:
 * `buildTraefikServiceTags` takes no visibility argument, so the router rule is a
 * bare `Host(…)` and the ForwardAuth middleware is attached in both states. What
 * enforces the difference is the tenant's `vibe/edge-authz/state` table, which
 * the agent republishes on each reconcile pass — so a change of visibility takes
 * effect when that table lands rather than by rewriting a route.
 *
 * Visibility is a STORED intent (a column on VibeApp), NOT derived from the
 * token's presence. That distinction is load-bearing: if it were derived, a
 * nulled/corrupt cipher would silently re-label a PRIVATE app as PUBLIC and the
 * deploy path would serve it open. Instead the deploy path reads the stored
 * visibility and fails closed for a PRIVATE app that has lost its token. The
 * create use case keeps the two consistent (PRIVATE ⇒ token, PUBLIC ⇒ none).
 * Default on create is PRIVATE (safe by default).
 */
// Sourced from the Prisma-generated enum values (single source of truth) so the
// API contract can never drift from the DB enum — add a value to the schema and
// it flows here automatically. Matches the `z.enum(DbEnum.*_VALUES)` pattern.
export const VibeAppVisibilitySchema = z.enum(DbEnum.VIBE_APP_VISIBILITY_VALUES);

/** `VibeAppVisibilitySchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeAppVisibilityReadSchema = enumReadSchema(DbEnum.VIBE_APP_VISIBILITY_VALUES);
export type VibeAppVisibility = z.infer<typeof VibeAppVisibilitySchema>;
